import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ScriptEditor } from '@components/script/ScriptEditor';
import { AIToolbar } from '@components/script/AIToolbar';
import { Button } from '@components/common/Button';
import { Loading } from '@components/common/Loading';
import { WorkflowSteps } from '@components/workflow/WorkflowSteps';
import NavigationBar from '@components/layout/NavigationBar';
import { useGetProjectQuery, useSaveScriptMutation } from '@services/projectsApi';
import { useToast } from '@hooks/useToast';
import { debounce } from '@utils/debounce';
export default function ScriptEditorPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { success: showSuccess, error: showError } = useToast();
    const { data: project, isLoading } = useGetProjectQuery(id);
    const [saveScript, { isLoading: isSaving }] = useSaveScriptMutation();
    const [content, setContent] = useState('');
    const [selectedText, setSelectedText] = useState({ start: 0, end: 0, text: '' });
    const [saveStatus, setSaveStatus] = useState('saved');
    const hasLoadedInitialContent = useRef(false);
    const editorRef = useRef(null);
    // 加载项目剧本 - 只在首次加载时设置内容，避免自动保存后的更新覆盖用户编辑
    useEffect(() => {
        if (project?.script?.content && !hasLoadedInitialContent.current) {
            console.log('首次加载剧本内容');
            setContent(project.script.content);
            hasLoadedInitialContent.current = true;
        }
    }, [project]);
    // 场景分析 - 识别以"场景"、"INT."、"EXT."等开头的行
    const scenes = useMemo(() => {
        const sceneList = [];
        const lines = content.split('\n');
        let currentScene = null;
        let lineIndex = 0;
        lines.forEach((line) => {
            const trimmedLine = line.trim();
            // 识别场景标题（以"场景"、"INT."、"EXT."、"内景"、"外景"开头）
            const isSceneHeader = /^场景\s*\d+/i.test(trimmedLine) ||
                /^(INT\.|EXT\.)/i.test(trimmedLine) ||
                /^(内景|外景)/i.test(trimmedLine) ||
                /^第[一二三四五六七八九十\d]+场/i.test(trimmedLine);
            if (isSceneHeader) {
                // 保存上一个场景
                if (currentScene) {
                    currentScene.endIndex = lineIndex;
                    currentScene.wordCount = content.substring(currentScene.startIndex, currentScene.endIndex).length;
                    sceneList.push(currentScene);
                }
                // 创建新场景
                currentScene = {
                    id: `scene-${sceneList.length + 1}`,
                    title: trimmedLine || `场景 ${sceneList.length + 1}`,
                    startIndex: lineIndex,
                    endIndex: content.length,
                    wordCount: 0
                };
            }
            lineIndex += line.length + 1; // +1 for newline
        });
        // 保存最后一个场景
        if (currentScene) {
            currentScene.endIndex = content.length;
            currentScene.wordCount = content.substring(currentScene.startIndex, currentScene.endIndex).length;
            sceneList.push(currentScene);
        }
        // 如果没有识别到场景，创建一个默认场景
        if (sceneList.length === 0 && content.trim()) {
            sceneList.push({
                id: 'scene-1',
                title: '全部内容',
                startIndex: 0,
                endIndex: content.length,
                wordCount: content.length
            });
        }
        return sceneList;
    }, [content]);
    // 计算统计信息
    const stats = useMemo(() => {
        const wordCount = content.length;
        // 估算时长：平均每分钟150字
        const estimatedMinutes = Math.ceil(wordCount / 150);
        return {
            wordCount,
            estimatedMinutes,
            sceneCount: scenes.length
        };
    }, [content, scenes.length]);
    // 跳转到场景
    const handleSceneClick = (scene) => {
        const textarea = editorRef.current;
        if (!textarea)
            return;
        textarea.focus();
        textarea.setSelectionRange(scene.startIndex, scene.startIndex);
        textarea.scrollTop = (scene.startIndex / content.length) * textarea.scrollHeight;
    };
    // 自动保存（防抖）- 延迟启动避免初始加载时触发
    const debouncedSave = useCallback(debounce(async (projectId, scriptContent) => {
        // 只有在初始内容加载完成后才允许自动保存
        if (!hasLoadedInitialContent.current) {
            console.log('跳过自动保存：初始内容尚未加载');
            return;
        }
        try {
            setSaveStatus('saving');
            await saveScript({ projectId, content: scriptContent }).unwrap();
            setSaveStatus('saved');
            console.log('自动保存成功');
        }
        catch (error) {
            setSaveStatus('unsaved');
            showError('自动保存失败');
        }
    }, 2000), [saveScript, showError]);
    // 内容变化时触发自动保存
    useEffect(() => {
        if (content && id && hasLoadedInitialContent.current && content !== project?.script?.content) {
            setSaveStatus('unsaved');
            debouncedSave(id, content);
        }
    }, [content, id, project?.script?.content, debouncedSave]);
    const handleManualSave = async () => {
        if (!id)
            return;
        try {
            setSaveStatus('saving');
            await saveScript({ projectId: id, content }).unwrap();
            setSaveStatus('saved');
            showSuccess('保存成功');
        }
        catch (error) {
            setSaveStatus('unsaved');
            showError('保存失败');
        }
    };
    const handleExport = () => {
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${project?.title || '剧本'}.txt`;
        a.click();
        URL.revokeObjectURL(url);
        showSuccess('导出成功');
    };
    const handleInsertText = (text) => {
        const textarea = editorRef.current;
        if (!textarea) {
            setContent(content + '\n\n' + text);
            return;
        }
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newContent = content.substring(0, start) + text + content.substring(end);
        setContent(newContent);
        // 设置光标位置
        setTimeout(() => {
            const newPosition = start + text.length;
            textarea.setSelectionRange(newPosition, newPosition);
            textarea.focus();
        }, 0);
    };
    const handleNext = () => {
        if (!id)
            return;
        navigate(`/project/${id}/casting`);
    };
    if (isLoading) {
        return _jsx(Loading, { fullScreen: true, text: "\u52A0\u8F7D\u4E2D..." });
    }
    if (!project) {
        return (_jsxs("div", { className: "flex min-h-[60vh] flex-col items-center justify-center", children: [_jsx("h2", { className: "mb-4 text-2xl font-semibold text-gray-800", children: "\u9879\u76EE\u4E0D\u5B58\u5728" }), _jsx(Button, { onClick: () => navigate('/topics'), children: "\u8FD4\u56DE\u9009\u9898" })] }));
    }
    return (_jsxs("div", { className: "fixed inset-0 flex flex-col bg-dark-700", children: [_jsx(NavigationBar, {}), _jsx("div", { className: "border-b border-gray-800 bg-dark-600", children: _jsx(WorkflowSteps, { projectId: id, currentStep: "script" }) }), _jsxs("div", { className: "flex items-center justify-between border-b border-gray-800 bg-dark-600 px-6 py-3", children: [_jsxs("div", { className: "flex items-center gap-4", children: [_jsx("h1", { className: "text-lg font-semibold text-gray-100", children: project.title }), _jsxs("span", { className: "text-sm text-gray-400", children: [saveStatus === 'saved' && '✓ 已保存', saveStatus === 'saving' && '⏳ 保存中...', saveStatus === 'unsaved' && '● 未保存'] })] }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx(Button, { variant: "ghost", size: "sm", onClick: () => navigate('/topics'), children: "\u2190 \u4E0A\u4E00\u6B65" }), _jsx(Button, { variant: "ghost", size: "sm", onClick: handleManualSave, loading: isSaving, children: "\u4FDD\u5B58" }), _jsx(Button, { variant: "ghost", size: "sm", onClick: handleExport, children: "\u5BFC\u51FA" }), _jsx(Button, { variant: "primary", size: "sm", onClick: handleNext, children: "\u4E0B\u4E00\u6B65\uFF1A\u9009\u89D2" })] })] }), _jsxs("div", { className: "flex flex-1 overflow-hidden", children: [_jsxs("div", { className: "flex flex-1 flex-col overflow-hidden", children: [_jsx(ScriptEditor, { ref: editorRef, value: content, onChange: setContent, onSelectionChange: setSelectedText, className: "flex-1" }), _jsxs("div", { className: "flex items-center justify-between border-t border-gray-800 bg-dark-600 px-6 py-2", children: [_jsxs("div", { className: "flex items-center gap-6 text-sm text-gray-400", children: [_jsxs("span", { children: ["\u5B57\u6570: ", stats.wordCount] }), _jsxs("span", { children: ["\u9884\u4F30\u65F6\u957F: ", stats.estimatedMinutes, " \u5206\u949F"] }), _jsxs("span", { children: ["\u573A\u666F\u6570: ", stats.sceneCount] })] }), scenes.length > 0 && (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-sm text-gray-400", children: "\u573A\u666F:" }), _jsx("div", { className: "flex gap-1 overflow-x-auto", children: scenes.map((scene, index) => (_jsx("button", { onClick: () => handleSceneClick(scene), className: "rounded bg-dark-500 px-3 py-1 text-sm text-gray-300 shadow-sm transition-colors hover:bg-primary-500/20 hover:text-primary-400 border border-gray-700", title: scene.title, children: index + 1 }, scene.id))) })] }))] })] }), _jsx("div", { className: "w-80 flex-shrink-0", children: _jsx(AIToolbar, { selectedText: selectedText.text, context: content, onInsertText: handleInsertText, projectId: id, projectTitle: project.title }) })] })] }));
}
